import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { MessageCircle, Search, Calendar, FileText, ArrowRight } from 'lucide-react';

const ChatWelcomeMessage = ({ onStartChat }) => {
  const steps = [
    {
      icon: MessageCircle,
      title: "Keskustelemme tarpeistasi",
      description: "Kerrot projektisi yksityiskohdat ja vaatimukset"
    },
    {
      icon: Search,
      title: "Haemme ehdokkaat",
      description: "Tekoäly löytää sopivimmat talentit tietokannastamme"
    },
    {
      icon: Calendar,
      title: "Sovitaan haastattelu",
      description: "Järjestämme tapaamisen valitsemiesi kanssa"
    },
    {
      icon: FileText,
      title: "Automaattiset sopimukset",
      description: "Tekoäly luo sopimuksen ja linkit allekirjoitukseen"
    }
  ];

  const handleStartChat = () => {
    const exampleMessage = "Hei! Etsin projektiimme React-kehittäjää. Projekti kestää noin 2-3 kuukautta ja työmäärä olisi noin 20h/viikko. Millaisia talentteja teillä on tarjolla?";
    if (onStartChat) {
      onStartChat(exampleMessage);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-2 sm:p-4">
      <Card className="w-full max-w-4xl bg-card/50 backdrop-blur border-0 shadow-2xl">
        <CardHeader className="text-center pb-3 px-4 sm:pb-4 sm:px-6">
          <CardTitle className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-2">
            Löydä täydellinen talentti
          </CardTitle>
          <CardDescription className="text-base sm:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            Tekoäly löytää sopivan henkilön minuuteissa — myös kiireellisiin projekteihin
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
          {/* Process Steps */}
          <div className="space-y-3 sm:space-y-4">
            <div className="grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-4">
              {steps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div 
                    key={index}
                    className="flex flex-col items-center text-center p-3 sm:p-4 bg-muted/20 rounded-xl sm:rounded-2xl hover:bg-muted/30 transition-all duration-300 hover:scale-105"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 sm:mb-3 shrink-0">
                      <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-xs sm:text-sm mb-1 sm:mb-2 text-foreground">
                        {step.title}
                      </h4>
                      <p className="text-xs text-muted-foreground leading-tight sm:leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Features - Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 py-3 sm:py-4">
            <div className="text-center">
              <div className="text-sm font-medium text-muted-foreground mb-2">Validoidut talentit</div>
              <div className="flex justify-center items-center mb-1">
                <Avatar className="w-12 h-12 sm:w-14 sm:h-14 border-2 sm:border-3 border-white shadow-lg sm:shadow-xl z-10">
                  <AvatarImage src="https://www.lenz.fi/wp-content/uploads/2024/11/Artboard-11@72x-600x600.png" alt="Talentti 1" />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">T1</AvatarFallback>
                </Avatar>
                <Avatar className="w-12 h-12 sm:w-14 sm:h-14 border-2 sm:border-3 border-white shadow-lg sm:shadow-xl -ml-5 sm:-ml-6 z-20">
                  <AvatarImage src="https://www.lenz.fi/wp-content/uploads/2024/11/Artboard-7@72x-600x600.png" alt="Talentti 2" />
                  <AvatarFallback className="bg-gradient-to-br from-green-500 to-teal-600 text-white text-sm">T2</AvatarFallback>
                </Avatar>
                <Avatar className="w-12 h-12 sm:w-14 sm:h-14 border-2 sm:border-3 border-white shadow-lg sm:shadow-xl -ml-5 sm:-ml-6 z-30">
                  <AvatarImage src="https://www.lenz.fi/wp-content/uploads/2024/11/Artboard-10@72x-600x600.png" alt="Talentti 3" />
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-600 text-white text-sm">T3</AvatarFallback>
                </Avatar>
                <div className="w-12 h-12 sm:w-14 sm:h-14 border-2 sm:border-3 border-white shadow-lg sm:shadow-xl -ml-5 sm:-ml-6 z-40 bg-muted/70 backdrop-blur rounded-full flex items-center justify-center">
                  <span className="text-lg sm:text-xl font-bold text-muted-foreground">...</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-muted-foreground mb-2">Nopeat tulokset</div>
              <div className="text-xl sm:text-2xl font-semibold text-primary">∼ 8 min</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-muted-foreground mb-2">Joustava tuntiperusteinen hinnoittelu</div>
              <div className="text-xl sm:text-2xl font-semibold text-primary">alkaen 40€/h</div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center space-y-3 pt-4 border-t border-muted/30">
            <p className="text-base font-medium text-muted-foreground">
              Aloitetaanko?
            </p>
            
            <Button 
              onClick={handleStartChat}
              className="px-8 py-3 text-base font-medium rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              size="lg"
            >
              <MessageCircle className="w-5 h-5 mr-3" />
              Kerro tarpeesi
              <ArrowRight className="w-5 h-5 ml-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatWelcomeMessage;